import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '../ui/Button';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { AiChatPanel } from '../ai/AiChatPanel';
import { useElementSelector } from '../../hooks/useElementSelector';
import { ElementSelectorOverlay } from '../ai/ElementSelectorOverlay';
import { useWorkspaces } from '../../hooks/useWorkspaces';
import { ThemeContext, useThemeState } from '../../hooks/useTheme';
import { ProductTour } from '../onboarding/ProductTour';
import { Plus, Sparkles, X, Cloud, BarChart2, TrendingUp, Zap, BookOpen } from 'lucide-react';
import type { Widget } from '../../hooks/useWorkspaces';
const VIEW_CONTEXT_MAP: Record<string, {
  name: string;
  description: string;
}> = {
  '/': {
    name: 'Home',
    description: 'The user is on the Overview/Home page with the Athena AI chat interface for energy trading intelligence. This is the main landing page.'
  },
  '/workspaces': {
    name: 'Workspaces Overview',
    description: 'The user is on their Workspaces overview page showing all their workspaces as cards. They can create, pin, favorite, and delete workspaces.'
  },
  '/workspaces': {
    name: 'Workspaces',
    description: 'The user is on their Workspaces dashboard with draggable widgets: wind forecast charts, interactive weather maps, and AI text blocks. Reference the visible widgets when answering.'
  },
  '/map': {
    name: 'Full-Screen Map',
    description: 'The user is viewing the full-screen interactive weather map of Europe. Visible controls: overlay selector (Air Temperature 2m, Air Pressure Sea Level, Geopotential Height 500hPa, Surface Solar Radiation), weather layer toggles (Temperature, Air Pressure, Cloud Cover, Precipitation, Wind Speed), Points of Interest panel (Wind Farms, Solar Farms, Oil & Gas, Ports, Airports, Nuclear Plants, Hydro, LNG Terminals, Interconnectors, Conflict Zones), model selector (EPT-2, EC-IFS, GFS), and a timeline playback control. The map shows live OpenWeatherMap tile overlays. Answer questions about what they see on the map.'
  },
  '/stations': {
    name: 'Weather Stations',
    description: 'The user is on the Weather Stations monitoring page. They can see station observation data, filters by country/parameter/status, and forecast accuracy benchmarks comparing model predictions against actual station measurements.'
  },
  '/energy': {
    name: 'Weather Forecast',
    description: 'The user is on the Weather Forecast page with multi-model forecast charts, model run selectors, location and variable pickers, and forecast delta comparisons. Models include EPT-2, EC-IFS, GFS, and others. Data includes wind speed, temperature, solar radiation, and more.'
  },
  '/intraday': {
    name: 'Intraday Trader Dashboard',
    description: 'The user is on the Intraday Trader dashboard. They can see: (1) A live news ticker with market alerts. (2) Three traffic light indicators showing Solar State (GREEN, 24.5 GW, +2.4%), Wind State (RED, 12.8 GW, -1.8% — model disagreement flagged), and Demand State (YELLOW, 68.2 GW, +2.4%). (3) A market summary showing German wind deviating -2.1 GW below forecast due to North Sea conditions, solar stable, demand elevated +1.5%. (4) Model picker with EPT-2, EC-IFS, GFS active. (5) Stacked sparkline charts for Wind Generation, Solar Generation, and Temperature comparing EPT-2 vs EC-IFS vs GFS over 24h. (6) A deviation heatmap showing model spread by hour (24h × 5 models). (7) A live signals feed with recent alerts. The Wind traffic light is RED — this is the primary alert requiring attention.'
  },
  '/notifications': {
    name: 'Notifications',
    description: 'The user is on the Notifications page viewing alerts, model updates, and market signals. They can filter by severity or category and mark items as read.'
  },
  '/overview': {
    name: 'Home',
    description: 'The user is on the Overview/Home page with the Athena AI chat interface for energy trading intelligence. This is the main landing page.'
  }
};
const WORKSPACE_TEMPLATES: {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  widgets: Widget[];
  counter: number;
}[] = [{
  id: 'meteorologist',
  name: 'Meteorologist',
  description: 'Weather analysis with maps, charts, and station data',
  icon: <Cloud size={18} className="text-[#EC4899]" data-id="element-763" />,
  counter: 5,
  widgets: [{
    i: 'map-1',
    x: 0,
    y: 0,
    w: 8,
    h: 6,
    type: 'map',
    config: {
      overlay: 'Geopotential Height 500hPa'
    }
  }, {
    i: 'chart-1',
    x: 8,
    y: 0,
    w: 4,
    h: 3,
    type: 'chart'
  }, {
    i: 'chart-2',
    x: 8,
    y: 3,
    w: 4,
    h: 3,
    type: 'chart'
  }, {
    i: 'table-1',
    x: 0,
    y: 6,
    w: 6,
    h: 4,
    type: 'table'
  }, {
    i: 'chart-3',
    x: 6,
    y: 6,
    w: 6,
    h: 4,
    type: 'chart'
  }]
}, {
  id: 'short-term-trader',
  name: 'Short Term Trader',
  description: 'Intraday price signals, demand forecasts, and model runs',
  icon: <Zap size={18} className="text-[#EC4899]" data-id="element-764" />,
  counter: 5,
  widgets: [{
    i: 'chart-1',
    x: 0,
    y: 0,
    w: 4,
    h: 4,
    type: 'chart'
  }, {
    i: 'chart-2',
    x: 4,
    y: 0,
    w: 8,
    h: 4,
    type: 'chart'
  }, {
    i: 'table-1',
    x: 0,
    y: 4,
    w: 6,
    h: 4,
    type: 'table'
  }, {
    i: 'chart-3',
    x: 6,
    y: 4,
    w: 6,
    h: 4,
    type: 'chart'
  }, {
    i: 'map-1',
    x: 0,
    y: 8,
    w: 12,
    h: 5,
    type: 'map'
  }]
}, {
  id: 'energy-analyst',
  name: 'Energy Analyst',
  description: 'Long-range forecasts, model comparisons, and reporting',
  icon: <BarChart2 size={18} className="text-[#EC4899]" data-id="element-765" />,
  counter: 5,
  widgets: [{
    i: 'chart-1',
    x: 0,
    y: 0,
    w: 6,
    h: 4,
    type: 'chart'
  }, {
    i: 'chart-2',
    x: 6,
    y: 0,
    w: 6,
    h: 4,
    type: 'chart'
  }, {
    i: 'table-1',
    x: 0,
    y: 4,
    w: 8,
    h: 4,
    type: 'table'
  }, {
    i: 'text-1',
    x: 8,
    y: 4,
    w: 4,
    h: 4,
    type: 'text',
    config: {
      prompt: 'Add your analysis notes here...'
    }
  }, {
    i: 'map-1',
    x: 0,
    y: 8,
    w: 12,
    h: 5,
    type: 'map',
    config: {
      overlay: 'Geopotential Height 500hPa'
    }
  }]
}, {
  id: 'energy-trader',
  name: 'Energy Trader',
  description: 'Multi-market price monitoring, generation mix, and live positions',
  icon: <TrendingUp size={18} className="text-[#EC4899]" data-id="element-766" />,
  counter: 7,
  widgets: [{
    i: 'chart-1',
    x: 0,
    y: 0,
    w: 5,
    h: 4,
    type: 'chart'
  }, {
    i: 'chart-2',
    x: 5,
    y: 0,
    w: 7,
    h: 4,
    type: 'chart'
  }, {
    i: 'table-1',
    x: 0,
    y: 4,
    w: 6,
    h: 4,
    type: 'table'
  }, {
    i: 'chart-3',
    x: 6,
    y: 4,
    w: 6,
    h: 4,
    type: 'chart'
  }, {
    i: 'map-1',
    x: 0,
    y: 8,
    w: 8,
    h: 5,
    type: 'map',
    config: {
      overlay: 'Geopotential Height 500hPa'
    }
  }, {
    i: 'text-1',
    x: 8,
    y: 8,
    w: 4,
    h: 5,
    type: 'text',
    config: {
      prompt: 'Log positions and market observations here...'
    }
  }, {
    i: 'chart-4',
    x: 0,
    y: 13,
    w: 12,
    h: 4,
    type: 'chart'
  }]
}];
export function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    createWorkspace,
    updateWidgetsById,
    renameWorkspace,
    workspaces
  } = useWorkspaces();
  const [isAiOpen, setIsAiOpen] = useState(false);
  const [aiMode, setAiMode] = useState<'docked' | 'floating'>('docked');
  const [dockedWidth, setDockedWidth] = useState(380);
  const [isAiResizing, setIsAiResizing] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  // Auto-close sidebar when viewport shrinks below sm breakpoint (640px)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 640px)');
    const handler = (e: MediaQueryListEvent) => {
      if (!e.matches) setIsSidebarOpen(false);
    };
    mq.addEventListener('change', handler);
    if (!mq.matches) setIsSidebarOpen(false);
    return () => mq.removeEventListener('change', handler);
  }, []);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createModalStep, setCreateModalStep] = useState<'choose' | 'template'>('choose');
  const themeState = useThemeState();
  const {
    isSelecting,
    hoveredRect,
    selectedElement,
    startSelecting,
    stopSelecting,
    clearSelection
  } = useElementSelector();
  const isDocked = isAiOpen && aiMode === 'docked';
  const viewContext = VIEW_CONTEXT_MAP[location.pathname] || {
    name: 'Unknown',
    description: 'The user is on an unrecognized page.'
  };
  const handleOpenCreateModal = () => {
    setCreateModalStep('choose');
    setCreateModalOpen(true);
  };
  const handleNewBlank = () => {
    setCreateModalOpen(false);
    const newId = createWorkspace();
    navigate(`/workspace/${newId}`, {
      state: {
        renaming: true,
        openAiPanel: true
      }
    });
  };
  const handleUseTemplate = (template: (typeof WORKSPACE_TEMPLATES)[number]) => {
    setCreateModalOpen(false);
    if (template.id === 'short-term-trader') {
      try {
        const existing = JSON.parse(localStorage.getItem('jua-pinned-pages') || '[]');
        if (!existing.includes('intraday')) {
          localStorage.setItem('jua-pinned-pages', JSON.stringify([...existing, 'intraday']));
        }
      } catch {}
      navigate('/intraday');
      return;
    }
    const baseName = template.name;
    const existingNames = new Set(workspaces.map(w => w.name));
    let uniqueName = baseName;
    let suffix = 2;
    while (existingNames.has(uniqueName)) {
      uniqueName = `${baseName} ${suffix}`;
      suffix++;
    }
    const newId = createWorkspace();
    setTimeout(() => {
      updateWidgetsById(newId, template.widgets, template.counter);
      renameWorkspace(newId, uniqueName);
      navigate(`/workspace/${newId}`, {
        state: {
          openAiPanel: true
        }
      });
    }, 0);
  };
  // Handle ESC key to cancel selection
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isSelecting) {
        stopSelecting();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSelecting, stopSelecting]);
  // Open AI panel when an element is selected
  useEffect(() => {
    if (selectedElement) {
      setIsAiOpen(true);
    }
  }, [selectedElement]);
  // Auto-open AI panel on workspace pages by default
  useEffect(() => {
    if (location.pathname.startsWith('/workspace/')) {
      setIsAiOpen(true);
    }
  }, [location.pathname]);
  // Auto-open AI panel from navigation state (e.g. from Overview "show me what you can do")
  useEffect(() => {
    const state = location.state as {
      openAiPanel?: boolean;
    } | null;
    if (state?.openAiPanel) {
      setIsAiOpen(true);
      // Clear the state so it doesn't re-trigger on refresh
      navigate(location.pathname, {
        replace: true,
        state: {}
      });
    }
  }, [location.state, location.pathname, navigate]);
  // Listen for workspace creation from Athena chat
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail) return;
      const {
        name,
        widgets: widgetTypes
      } = detail as {
        name: string;
        widgets: {
          type: string;
        }[];
      };
      const wsName = name || 'AI Workspace';
      const existingNames = new Set(workspaces.map(w => w.name));
      let uniqueName = wsName;
      let suffix = 2;
      while (existingNames.has(uniqueName)) {
        uniqueName = `${wsName} ${suffix}`;
        suffix++;
      }
      // Build widget layout from types
      const validTypes = ['chart', 'map', 'table', 'text'];
      const filtered = (widgetTypes || []).filter(w => validTypes.includes(w.type));
      const layoutWidgets = filtered.map((w, i) => {
        const col = i % 2 * 6;
        const row = Math.floor(i / 2) * 5;
        return {
          i: `${w.type}-ai-${i + 1}`,
          x: col,
          y: row,
          w: 6,
          h: 5,
          type: w.type as 'chart' | 'map' | 'table' | 'text'
        };
      });
      if (layoutWidgets.length === 0) {
        layoutWidgets.push({
          i: 'chart-ai-1',
          x: 0,
          y: 0,
          w: 6,
          h: 5,
          type: 'chart' as const
        }, {
          i: 'map-ai-1',
          x: 6,
          y: 0,
          w: 6,
          h: 5,
          type: 'map' as const
        });
      }
      const newId = createWorkspace();
      setTimeout(() => {
        updateWidgetsById(newId, layoutWidgets, layoutWidgets.length);
        renameWorkspace(newId, uniqueName);
        navigate(`/workspace/${newId}`, {
          state: {
            openAiPanel: true
          }
        });
      }, 0);
    };
    window.addEventListener('jua:create-workspace-from-ai', handler);
    return () => window.removeEventListener('jua:create-workspace-from-ai', handler);
  }, [workspaces, createWorkspace, updateWidgetsById, renameWorkspace, navigate]);
  // Listen for "Edit with AI" events dispatched from widgets
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail) return;
      // Reconstruct a DOMRect-like object
      const rect = {
        top: detail.rect.top,
        left: detail.rect.left,
        right: detail.rect.right,
        bottom: detail.rect.bottom,
        width: detail.rect.width,
        height: detail.rect.height,
        x: detail.rect.x,
        y: detail.rect.y,
        toJSON: () => detail.rect
      } as DOMRect;
      clearSelection();
      // Small delay so clearSelection settles before setting new element
      setTimeout(() => {
        // Directly set selected element via the selector hook's internal setter
        // We trigger it by simulating what startSelecting + click does:
        // Use a workaround: dispatch a synthetic selectedElement via a ref
        setIsAiOpen(true);
        (window as any).__juaSetSelectedElement?.({
          description: detail.description,
          textContent: detail.textContent,
          tagName: detail.tagName,
          rect,
          elementType: detail.elementType
        });
      }, 50);
    };
    window.addEventListener('jua:edit-with-ai', handler);
    return () => window.removeEventListener('jua:edit-with-ai', handler);
  }, [clearSelection]);
  const isLight = themeState.theme === 'light';
  return <ThemeContext.Provider value={themeState} data-id="element-767">
      <div className="app-root min-h-screen font-sans selection:bg-[#EC4899]/30 selection:text-[#EC4899]" style={{
      color: isLight ? '#111827' : '#f1f5f9',
      position: 'relative'
    }} data-id="element-768">
        {/* ── Light mode background: fixed gradient + animated orbs ── */}
        {isLight && <div className="fixed inset-0 pointer-events-none" style={{
        zIndex: 0,
        background: 'linear-gradient(to bottom right, #fefafc, #fdfaff, #f5feff)'
      }} data-id="element-769">
            {/* Top-right orb */}
            <div className="absolute animate-pulse" style={{
          top: '-200px',
          right: '-200px',
          width: '800px',
          height: '800px',
          borderRadius: '50%',
          background: 'radial-gradient(ellipse at center, rgba(249,168,212,0.15), rgba(216,180,254,0.15), transparent 70%)',
          filter: 'blur(60px)'
        }} data-id="element-770" />
            {/* Bottom-left orb */}
            <div className="absolute animate-pulse" style={{
          bottom: '-150px',
          left: '-150px',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: 'radial-gradient(ellipse at center, rgba(103,232,249,0.10), rgba(216,180,254,0.10), transparent 70%)',
          filter: 'blur(60px)',
          animationDelay: '1s'
        }} data-id="element-771" />
          </div>}

        {/* ── Dark mode background ── */}
        {!isLight && <div className="fixed inset-0 pointer-events-none" style={{
        zIndex: 0,
        background: '#0B1120'
      }} data-id="element-772" />}

        {/* All content sits above the background */}
        <div className="relative" style={{
        zIndex: 1
      }} data-id="element-773">
          <Sidebar isOpen={isSidebarOpen} onToggle={() => setIsSidebarOpen(!isSidebarOpen)} data-id="element-774" />
          {isSidebarOpen && <div className="fixed inset-0 z-30 bg-black/50 sm:hidden" onClick={() => setIsSidebarOpen(false)} data-id="element-775" />}
          <TopBar onToggleAi={() => setIsAiOpen(!isAiOpen)} onOpenAi={() => setIsAiOpen(true)} isAiOpen={isDocked} dockedWidth={dockedWidth} isSidebarOpen={isSidebarOpen} onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} onCreateWorkspace={handleOpenCreateModal} data-id="element-776" />
          <main className={cn('pt-16 min-h-screen', !isAiResizing && 'transition-all duration-300 ease-in-out')} style={{
          paddingLeft: isSidebarOpen ? '240px' : '0',
          marginRight: isDocked ? `${dockedWidth}px` : '0',
          backgroundColor: 'transparent'
        }} data-id="element-777">
            <Outlet data-id="element-778" />
          </main>
          <ElementSelectorOverlay isSelecting={isSelecting} hoveredRect={hoveredRect} data-id="element-779" />
          <AiChatPanel isOpen={isAiOpen} mode={aiMode} onClose={() => setIsAiOpen(false)} onModeChange={setAiMode} onDockedWidthChange={setDockedWidth} onDockedResizingChange={setIsAiResizing} onStartSelection={() => {
          startSelecting();
        }} selectedElement={selectedElement} onClearSelection={clearSelection} viewContext={viewContext} data-id="element-780" />

          <ProductTour data-id="element-781" />

          {/* Global New Workspace Modal */}
          {createModalOpen && <div className="jua-ws-modal-overlay fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setCreateModalOpen(false)} data-id="element-782">
              <div className={cn('jua-ws-modal w-full max-w-lg rounded-2xl border shadow-2xl overflow-hidden', isLight ? 'border-slate-200 shadow-black/10' : 'border-white/[0.08] shadow-black/60')} style={{
            background: isLight ? 'rgba(255, 255, 255, 0.98)' : 'rgba(15, 23, 42, 0.97)',
            backdropFilter: 'blur(40px)'
          }} onClick={e => e.stopPropagation()} data-id="element-783">
                <div className={cn('flex items-center justify-between px-6 py-4 border-b', isLight ? 'border-slate-200' : 'border-white/[0.06]')} data-id="element-784">
                  <div data-id="element-785">
                    <h2 className={cn('text-base font-semibold', isLight ? 'text-slate-900' : 'text-white')} data-id="element-786">
                      New Workspace
                    </h2>
                    <p className={cn('text-xs mt-0.5', isLight ? 'text-slate-500' : 'text-slate-400')} data-id="element-787">
                      Start blank or pick a template
                    </p>
                  </div>
                  <button onClick={() => setCreateModalOpen(false)} className={cn('p-1.5 rounded-lg transition-colors', isLight ? 'text-slate-400 hover:text-slate-700 hover:bg-slate-100' : 'text-white/40 hover:text-white hover:bg-white/[0.08]')} data-id="element-788">
                    <X size={16} data-id="element-789" />
                  </button>
                </div>
                <div className="p-5" data-id="element-790">
                  {/* Template Cards */}
                  <div className="grid grid-cols-2 gap-3" data-id="element-791">
                    {WORKSPACE_TEMPLATES.map(tpl => <button key={tpl.id} onClick={() => handleUseTemplate(tpl)} className={cn('flex flex-col items-center gap-3 p-5 rounded-xl border transition-all group text-center', isLight ? 'border-slate-200 hover:border-[#EC4899]/40 bg-slate-50/50 hover:bg-[#EC4899]/[0.04]' : 'border-white/[0.08] hover:border-[#EC4899]/40 bg-white/[0.03] hover:bg-[#EC4899]/[0.06]')} data-id="element-792">
                        <div className={cn('w-12 h-12 rounded-xl group-hover:bg-[#EC4899]/10 border group-hover:border-[#EC4899]/20 flex items-center justify-center transition-all', isLight ? 'bg-slate-100 border-slate-200' : 'bg-slate-800 border-white/[0.06]')} data-id="element-793">
                          {tpl.icon}
                        </div>
                        <div data-id="element-794">
                          <div className={cn('text-sm font-semibold group-hover:text-[#EC4899] transition-colors mb-1', isLight ? 'text-slate-800' : 'text-white')} data-id="element-795">
                            {tpl.name}
                          </div>
                          <div className={cn('text-[11px] leading-relaxed', isLight ? 'text-slate-500' : 'text-slate-400')} data-id="element-796">
                            {tpl.description}
                          </div>
                        </div>
                        <div className={cn('text-[10px] mt-auto', isLight ? 'text-slate-400' : 'text-slate-500')} data-id="element-797">
                          {tpl.widgets.length} widget
                          {tpl.widgets.length !== 1 ? 's' : ''}
                        </div>
                      </button>)}
                  </div>

                  {/* Blank — full width, separated */}
                  <button onClick={handleNewBlank} className={cn('w-full flex items-center gap-3 mt-3 px-4 py-3 rounded-xl border border-dashed transition-all group', isLight ? 'border-slate-300 hover:border-[#EC4899]/40 bg-slate-50/30 hover:bg-[#EC4899]/[0.03]' : 'border-white/[0.1] hover:border-[#EC4899]/40 bg-white/[0.02] hover:bg-[#EC4899]/[0.04]')} data-id="element-798">
                    <div className={cn('w-9 h-9 rounded-lg group-hover:bg-[#EC4899]/10 border group-hover:border-[#EC4899]/20 flex items-center justify-center transition-all flex-shrink-0', isLight ? 'bg-slate-100 border-slate-200' : 'bg-slate-800 border-white/[0.06]')} data-id="element-799">
                      <Plus size={18} className="text-[#EC4899] transition-colors" data-id="element-800" />
                    </div>
                    <div className="text-left" data-id="element-801">
                      <div className={cn('text-sm font-semibold group-hover:text-[#EC4899] transition-colors', isLight ? 'text-slate-800' : 'text-white')} data-id="element-802">
                        Blank workspace
                      </div>
                      <div className={cn('text-[11px]', isLight ? 'text-slate-500' : 'text-slate-400')} data-id="element-803">
                        Start from scratch with an empty canvas
                      </div>
                    </div>
                  </button>

                  {/* Learn how workspaces work */}
                  <button onClick={() => {
                setCreateModalOpen(false);
                const newId = createWorkspace();
                updateWidgetsById(newId, [{
                  i: 'chart-demo-1',
                  x: 0,
                  y: 0,
                  w: 6,
                  h: 5,
                  type: 'chart'
                }, {
                  i: 'map-demo-1',
                  x: 6,
                  y: 0,
                  w: 6,
                  h: 5,
                  type: 'map'
                }], 2);
                renameWorkspace(newId, 'Workspace Tutorial');
                navigate(`/workspace/${newId}`, {
                  state: {
                    workspaceTour: true
                  }
                });
              }} className={cn('w-full flex items-center gap-3 mt-2 px-4 py-3 rounded-xl border border-dashed transition-all group', isLight ? 'border-slate-300 hover:border-purple-400/40 bg-slate-50/30 hover:bg-purple-500/[0.03]' : 'border-white/[0.1] hover:border-purple-400/40 bg-white/[0.02] hover:bg-purple-500/[0.04]')} data-id="element-804">
                    <div className={cn('w-9 h-9 rounded-lg group-hover:bg-purple-500/10 border group-hover:border-purple-400/20 flex items-center justify-center transition-all flex-shrink-0', isLight ? 'bg-slate-100 border-slate-200' : 'bg-slate-800 border-white/[0.06]')} data-id="element-805">
                      <BookOpen size={18} className="text-purple-400 transition-colors" data-id="element-806" />
                    </div>
                    <div className="text-left" data-id="element-807">
                      <div className={cn('text-sm font-semibold group-hover:text-purple-400 transition-colors', isLight ? 'text-slate-800' : 'text-white')} data-id="element-808">
                        Learn how workspaces work
                      </div>
                      <div className={cn('text-[11px]', isLight ? 'text-slate-500' : 'text-slate-400')} data-id="element-809">
                        Take a guided tour of workspace features
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </div>}
        </div>
      </div>
    </ThemeContext.Provider>;
}